/**
 * Post Grid — sorting and paging without a page reload.
 *
 * Both controls are plain links and a plain GET form, so the listing still
 * works with no scripting; this takes their events over and asks admin-ajax
 * for the same markup the block would have rendered, then swaps it in.
 * Everything else is untouched: the request carries the order and the page the
 * reader is on, the address bar ends up exactly where the control would have
 * left it, and the pagination that comes back is built server side, so the
 * links stay plain links.
 */

/**
 * The page a listing URL points at, however the site paginates.
 *
 * @param {URL} url Listing URL.
 * @return {number} Page number, never below one.
 */
const pageFromUrl = ( url ) => {
	const segment = url.pathname.match( /\/page\/(\d+)\/?$/ );
	const value = segment
		? segment[ 1 ]
		: url.searchParams.get( 'paged' ) || url.searchParams.get( 'page' );

	return Math.max( 1, parseInt( value, 10 ) || 1 );
};

/**
 * The order a listing URL asks for.
 *
 * @param {URL} url Listing URL.
 * @return {string} 'newest' or 'oldest'.
 */
const sortFromUrl = ( url ) =>
	'oldest' === url.searchParams.get( 'sort' ) ? 'oldest' : 'newest';

const setup = ( root ) => {
	const select = root.querySelector( '[data-post-grid-sort]' );
	const results = root.querySelector( '[data-post-grid-results]' );
	const endpoint = root.dataset.postGridEndpoint;

	if ( ! select || ! results || ! endpoint ) {
		return;
	}

	// The markup submits the form on change so the order works before this
	// script arrives. From here the fetch below does that job instead.
	select.removeAttribute( 'onchange' );

	let inflight = null;

	// The site header pins itself to the top of the viewport once the page has
	// scrolled, so bringing the block flush to the viewport top would tuck the
	// heading underneath it. Measure the header as it actually is - its stuck
	// height differs by breakpoint - and stop short of it. The section's own
	// top padding supplies the breathing room from there.
	const scrollToTop = () => {
		const header = document.querySelector( '[data-site-header]' );
		const offset = header ? header.getBoundingClientRect().height : 0;
		const top = root.getBoundingClientRect().top + window.scrollY - offset;

		// Lenis owns the document scroll when it is running; a native smooth
		// scroll alongside it would fight its frame loop, so hand it the target.
		if ( window.bfLenis ) {
			window.bfLenis.scrollTo( Math.max( 0, top ) );
			return;
		}

		window.scrollTo( { top: Math.max( 0, top ), behavior: 'smooth' } );
	};

	const load = ( sort, paged, url, push, scroll ) => {
		const request = new URL( endpoint, window.location.href );

		request.searchParams.set( 'action', 'bf_post_grid' );
		request.searchParams.set( 'sort', sort );
		request.searchParams.set( 'paged', paged );
		request.searchParams.set( 'per_page', root.dataset.postGridPerPage || '' );
		request.searchParams.set( 'url', url.toString() );

		// A reader who changes their mind mid-request only wants the last answer.
		inflight?.abort();
		inflight = new AbortController();

		results.setAttribute( 'aria-busy', 'true' );

		fetch( request, { credentials: 'same-origin', signal: inflight.signal } )
			.then( ( response ) =>
				response.ok
					? response.text()
					: Promise.reject( new Error( `HTTP ${ response.status }` ) )
			)
			.then( ( html ) => {
				results.innerHTML = html;
				results.setAttribute( 'aria-busy', 'false' );

				// The page the reader is on now, so a later sort stays on it the
				// way it did when paging reloaded the page.
				root.dataset.postGridPage = String( paged );

				if ( push ) {
					window.history.pushState(
						{ bfPostGridSort: sort, bfPostGridPage: paged },
						'',
						url
					);
				}

				// A page load lands at the top of the listing; a swap should not
				// leave the reader at the foot of a grid that has changed under
				// them.
				if ( scroll ) {
					scrollToTop();
				}
			} )
			.catch( ( error ) => {
				if ( 'AbortError' === error.name ) {
					return;
				}

				// Anything else and the reader still gets their results — the
				// plain page load the control had before.
				window.location.assign( url );
			} );
	};

	const sortNow = () => {
		const url = new URL( window.location.href );

		url.searchParams.set( 'sort', select.value );
		load( select.value, root.dataset.postGridPage || '1', url, true, false );
	};

	select.addEventListener( 'change', sortNow );

	select.form?.addEventListener( 'submit', ( event ) => {
		event.preventDefault();
		sortNow();
	} );

	// The pagination is replaced with every set of results, so the listener
	// lives on the wrapper rather than on the links themselves. Only the
	// numbers are anchors — the current page and the ellipsis are spans.
	results.addEventListener( 'click', ( event ) => {
		const link = event.target.closest(
			'.block-post-grid__pagination a[href]'
		);

		if (
			! link ||
			! results.contains( link ) ||
			event.defaultPrevented ||
			0 !== event.button ||
			event.metaKey ||
			event.ctrlKey ||
			event.shiftKey ||
			event.altKey
		) {
			return;
		}

		const url = new URL( link.href, window.location.href );

		// Anything off this site, or opening elsewhere, is the browser's.
		if ( url.origin !== window.location.origin || link.target ) {
			return;
		}

		event.preventDefault();
		load( sortFromUrl( url ), pageFromUrl( url ), url, true, true );
	} );

	// Back and forward move between orders and pages rather than out of the
	// page.
	window.addEventListener( 'popstate', () => {
		const url = new URL( window.location.href );
		const sort = sortFromUrl( url );
		const paged = pageFromUrl( url );

		if (
			sort === select.value &&
			String( paged ) === ( root.dataset.postGridPage || '1' )
		) {
			return;
		}

		select.value = sort;
		load( sort, paged, url, false, false );
	} );
};

document.querySelectorAll( '[data-post-grid]' ).forEach( setup );
